import {isDistinctCanvasImageId} from '../../../../shared/savedBrandAdCanvas';
import {canvasSourceAssociations} from './canvas-source-association';
import {persistedCanvasImageId} from './canvas-run-recovery';

export type ImageGeometry= {x:number;y:number;displayWidth:number;displayHeight:number};
type Entry={version:1;projectId:number;revision:string;savedAt:number;positions:Record<string,ImageGeometry>};
type StorageLike=Pick<Storage,'getItem'|'setItem'|'removeItem'>;
type RecoveryImage={id:string;src?:string;sourceImageId?:string;x:number;y:number;displayWidth:number;displayHeight:number};
type PersistedRecoveryImage={clientId?:string|null;client_id?:string|null;id?:number|string;src?:string|null;image_url?:string|null;x:number;y:number;displayWidth?:number;display_width?:number;displayHeight?:number;display_height?:number;variantOfImageId?:number|string|null;variant_of_image_id?:number|string|null};
export type ReapplyGeometryEntry={sourceId:string;targetId:string;geometry:ImageGeometry;base:ImageGeometry|null;image:RecoveryImage;row?:PersistedRecoveryImage};

/** Combine durable recovered edits with live same-tab intent and resolve only safe legacy aliases. */
export function planImageGeometryReapply(
 images:RecoveryImage[],rows:PersistedRecoveryImage[],journalPositions:Record<string,ImageGeometry>,localIntentIds:ReadonlySet<string>,
):ReapplyGeometryEntry[]{
 const imageById=new Map(images.map(image=>[image.id,image])),sources=canvasSourceAssociations(rows);
 const ids=new Set([...Object.keys(journalPositions),...Array.from(localIntentIds)]),plan:ReapplyGeometryEntry[]=[];
 for(const sourceId of Array.from(ids)){
  const image=imageById.get(sourceId);if(!image)continue;
  const geometry=localIntentIds.has(sourceId)?image:journalPositions[sourceId];
  if(!geometry)continue;
  let row=rows.find(item=>persistedCanvasImageId(item)===sourceId);
  if(!row&&image.src&&!isDistinctCanvasImageId(sourceId)){
   const matches=rows.filter(item=>{
    const id=persistedCanvasImageId(item);
    return !!id&&!isDistinctCanvasImageId(id)&&(item.src??item.image_url??'')===image.src&&
      (sources.get(id)??null)===(image.sourceImageId??null);
   });
   if(matches.length===1)row=matches[0];
  }
  const targetId=row?persistedCanvasImageId(row):sourceId;
  const base=row?{x:row.x,y:row.y,displayWidth:row.displayWidth??row.display_width!,displayHeight:row.displayHeight??row.display_height!}:null;
  plan.push({sourceId,targetId,geometry:{x:geometry.x,y:geometry.y,displayWidth:geometry.displayWidth,displayHeight:geometry.displayHeight},base,image,row});
 }
 return plan;
}

/** Apply a resolved Reapply plan while replacing only explicitly matched aliases. */
export function applyImageGeometryReapplyPlan<T extends RecoveryImage>(images:T[],plan:ReapplyGeometryEntry[],restore:(entry:ReapplyGeometryEntry)=>T):T[]{
 const aliases=new Map(plan.filter(entry=>entry.sourceId!==entry.targetId).map(entry=>[entry.sourceId,entry.targetId]));
 const byTarget=new Map(plan.map(entry=>[entry.targetId,entry]));
 const existing=new Set(images.map(image=>image.id));
 const next=images.filter(image=>!aliases.has(image.id)).map(image=>{
  const entry=byTarget.get(image.id);return entry?{...image,...entry.geometry}:image;
 });
 for(const entry of plan)if(!existing.has(entry.targetId)&&entry.row)next.push({...restore(entry),...entry.geometry});
 return next;
}
/** Per-tab unsaved edit intent, never image bytes, URLs or brand/chat content.
 * A full document navigation destroys the in-memory HTTP queue. Matching ACKs
 * remove intent; a subsequent owned image read can safely retry existing tiles.
 */
export function createImageGeometryJournal(storage:()=>StorageLike|undefined,now=Date.now,revision=()=>crypto.randomUUID()){
 const key=(id:number)=>`canvas-unsaved-geometry-v1:${id}`;
 const validId=(id:number)=>Number.isSafeInteger(id)&&id>0;
 const validGeometry=(v:any):v is ImageGeometry=>v&&['x','y','displayWidth','displayHeight'].every(k=>typeof v[k]==='number'&&Number.isFinite(v[k])&&Math.abs(v[k])<=10_000_000)&&v.displayWidth>0&&v.displayHeight>0;
 function read(projectId:number):Entry|undefined{
  if(!validId(projectId))return;
  try{
   const raw=storage()?.getItem(key(projectId));if(!raw||raw.length>512_000)return;
   const entry=JSON.parse(raw);
   if(entry?.version!==1||entry.projectId!==projectId||typeof entry.revision!=='string'||entry.revision.length>100||!Number.isFinite(entry.savedAt)||entry.savedAt>now()+60_000||now()-entry.savedAt>86_400_000||!entry.positions||typeof entry.positions!=='object'||Array.isArray(entry.positions))return;
   const positions=Object.entries(entry.positions);if(positions.length>1000||positions.some(([id,value])=>!id||id.length>300||!validGeometry(value)))return;
   const clean:Record<string,ImageGeometry>=Object.create(null);
   for(const [id,value] of positions){const g=value as ImageGeometry;clean[id]={x:g.x,y:g.y,displayWidth:g.displayWidth,displayHeight:g.displayHeight};}
   return{version:1,projectId,revision:entry.revision,savedAt:entry.savedAt,positions:clean};
  }catch{return;}
 }
 function ack(projectId:number,expected:string,ids?:ReadonlySet<string>){try{
  const current=read(projectId);if(current?.revision!==expected)return;
  if(!ids){storage()?.removeItem(key(projectId));return;}
  const positions:Record<string,ImageGeometry>=Object.create(null);
  for(const [id,geometry] of Object.entries(current.positions))if(!ids.has(id))positions[id]=geometry;
  if(!Object.keys(positions).length){storage()?.removeItem(key(projectId));return;}
  storage()?.setItem(key(projectId),JSON.stringify({...current,revision:revision(),savedAt:now(),positions}));
 }catch{}}
 function stage(projectId:number,images:Array<ImageGeometry&{id:string}>,dirty:ReadonlySet<string>):Entry|undefined{
  if(!validId(projectId))return;
  const prior=read(projectId),positions:Record<string,ImageGeometry>=Object.create(null);
  for(const image of images){
   if(!validGeometry(image))continue;
   // The full canvas may currently show server geometry for recovered entries.
   // Only a node in this edit batch can replace its pending journal geometry.
   const geometry=!dirty.has(image.id)&&prior?.positions[image.id]?prior.positions[image.id]:image;
   if(dirty.has(image.id)||prior?.positions[image.id])positions[image.id]={x:geometry.x,y:geometry.y,displayWidth:geometry.displayWidth,displayHeight:geometry.displayHeight};
  }
  if(!Object.keys(positions).length){if(prior)ack(projectId,prior.revision);return;}
  if(Object.keys(positions).length>1000)return;
  const entry:Entry={version:1,projectId,revision:revision(),savedAt:now(),positions};
  try{const target=storage();if(!target)return;target.setItem(key(projectId),JSON.stringify(entry));return entry;}catch{return;}
 }
 return{read,stage,ack};
}
